/*
 * Copyright 2026 Sony Corporation
 */
import { USAGE_WARNING_PERCENT, type Usage } from '@ips-jit/schema';
import { Alert, AlertTitle, Box, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';

import { useUsage } from '../contexts/usage-context';
import { formatPercent, formatResetDay, formatTokens } from '../services/usage';

const usedOf = (usage: Usage) => `${formatTokens(usage.tokens)} of ${formatTokens(usage.limit_tokens)} tokens`;

/**
 * The alert at the top of every page once the user's monthly token limit is reached (the assistant has stopped), and a
 * warning, which can be put away, when it is close.
 */
export const UsageBanner = () => {
  const usage = useUsage()?.usage;
  const [dismissed, setDismissed] = useState<string>();
  if (!usage) {
    return null;
  }
  if (usage.blocked) {
    return (
      <Alert severity="error" role="alert" sx={{ borderRadius: 0, mb: 1 }}>
        <AlertTitle>Token limit reached — the assistant has stopped</AlertTitle>
        You have used {usedOf(usage)} ({formatPercent(usage.percent)}) this month. New requests, roadmap steps and
        automatic fixes start again on {formatResetDay(usage.resets_at)}, or as soon as an administrator raises your
        limit. Your apps and runtimes keep working.
      </Alert>
    );
  }
  const key = `${usage.period}:${Math.floor(usage.percent / 10)}`;
  if (usage.percent >= USAGE_WARNING_PERCENT && dismissed !== key) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 0, mb: 1 }} onClose={() => setDismissed(key)}>
        You have used {formatPercent(usage.percent)} of your monthly token limit ({usedOf(usage)}). The assistant stops
        when it is used up.
      </Alert>
    );
  }
  return null;
};

/** The user's usage in the account menu: tokens used, the limit and the share, with a bar. */
export const UsageSummary = () => {
  const usage = useUsage()?.usage;
  if (!usage) {
    return null;
  }
  const color = usage.blocked ? 'error' : usage.percent >= USAGE_WARNING_PERCENT ? 'warning' : 'primary';
  return (
    <Box sx={{ px: 2, py: 1, minWidth: 240 }} aria-label="Token usage this month">
      <Typography variant="caption" color="text.secondary">
        Tokens this month
      </Typography>
      <Typography variant="body2">
        {usedOf(usage)} · {formatPercent(usage.percent)}
      </Typography>
      <LinearProgress
        variant="determinate"
        color={color}
        value={Math.min(100, usage.percent)}
        aria-label="Share of the monthly token limit used"
        sx={{ my: 0.5 }}
      />
      <Typography variant="caption" color={usage.blocked ? 'error' : 'text.secondary'}>
        {usage.blocked ? 'Limit reached. ' : ''}Resets on {formatResetDay(usage.resets_at)}
      </Typography>
    </Box>
  );
};
